import { InfoCircledIcon } from '@radix-ui/react-icons';
import {
  Badge,
  Card,
  Flex,
  Grid,
  Heading,
  Skeleton,
  Text,
} from '@radix-ui/themes';
import { CommonTooltip } from 'components/common/tooltip';
import { formatDate } from 'components/sections/admin-portal/machines/dialogs/settings/machine-status/common';
import { MachineUser } from 'components/sections/admin-portal/machines/machine-user';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { format } from 'date-fns-tz';
import { t } from 'i18next';
import { UserRole } from 'lib_ts/enums/auth.enums';
import { LOCAL_VERBOSE_DATETIME_FORMAT } from 'lib_ts/enums/datetime.enums';
import { ServiceCategory } from 'lib_ts/enums/machine-service-logs.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { TrackingDevice } from 'lib_ts/enums/training.enums';
import { isNil, maxBy } from 'lodash';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { ShotsService } from 'services/shots.service';
import { useAuthStore } from 'stores/use-auth-store';

const LabelledData = (props: { label: string; children: ReactNode }) => {
  return (
    <Flex direction="column" gap="1">
      <Text color={RADIX.COLOR.NEUTRAL}>{t(props.label)}</Text>
      {props.children}
    </Flex>
  );
};

export const MachineHardwareSection = () => {
  // the first poll for updating machine details will trigger a loading state (loadingMachineDetails)
  // we'll use this to show a loading skeleton for some Hardware specific fields we won't have until the dialog is opened
  const machine = useMachinesPortalStore((s) => s.machine);
  const lastCompletedRT = useMachinesPortalStore((s) => s.lastCompletedRT);
  const lastCompletedVcc = useMachinesPortalStore((s) => s.lastCompletedVcc);
  const serviceLogs = useMachinesPortalStore((s) => s.serviceLogs);
  const loadingMachineDetails = useMachinesPortalStore(
    (s) => s.loadingMachineDetails
  );

  const current = useAuthStore((s) => s.current);

  const isAdmin = current.role === UserRole.admin;

  const lastBallChange = useMemo(
    () =>
      maxBy(
        serviceLogs.filter(
          (m) =>
            m.category === ServiceCategory.BallReplacement &&
            m.ball_type === machine?.ball_type
        ),
        (m) => m.serviced
      ),
    [serviceLogs, machine?.ball_type]
  );

  const lastWheelChange = useMemo(
    () =>
      maxBy(
        serviceLogs.filter((m) => m.category === ServiceCategory.WheelSwap),
        (m) => m.serviced
      ),
    [serviceLogs]
  );

  // how many shots have been recorded (for the specified ball type) since the change
  const [ballChangeShots, setBallChangeShots] = useState<number>();

  useEffect(() => {
    setBallChangeShots(undefined);

    if (!machine?.machineID) {
      return;
    }

    if (!lastBallChange?.serviced) {
      return;
    }

    ShotsService.getInstance()
      .countShotsSince({
        machineID: machine?.machineID,
        start_date: lastBallChange.serviced,
        ball_type: lastBallChange.ball_type,
      })
      .then((result) => setBallChangeShots(result));
  }, [machine?.machineID, lastBallChange?.serviced]);

  // how many shots have been recorded (for any ball type) since the change
  const [wheelChangeFires, setWheelChangeFires] = useState<number>();

  useEffect(() => {
    setWheelChangeFires(undefined);

    // wait until service logs are loaded so lastWheelChange (and therefore
    // sinceDate) is accurate on the first run, otherwise we'd fire a redundant
    // request against machine._created before the real wheel change date loads
    if (loadingMachineDetails || !machine?.machineID) {
      return;
    }

    const sinceDate = lastWheelChange?.serviced ?? machine._created;

    if (!sinceDate) {
      return;
    }

    let cancelled = false;

    ShotsService.getInstance()
      .countFiresSince({
        machineID: machine?.machineID,
        start_date: sinceDate,
        ball_type: null,
      })
      .then((result) => {
        if (!cancelled) {
          setWheelChangeFires(result);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    loadingMachineDetails,
    machine?.machineID,
    machine?._created,
    lastWheelChange?.serviced,
  ]);

  if (!machine) {
    return null;
  }

  const repeatabilityTestTooltipContent =
    lastCompletedRT &&
    [
      `**${t('admin.id')}**: ${lastCompletedRT._id}`,
      `**${t('common.machine')}**: ${lastCompletedRT.machineID}`,
      // _changed should be the date the RT was completed
      `**${t('common.date')}**: ${formatDate(lastCompletedRT._changed)}`,
      `**${t('common.ball-type')}**: ${lastCompletedRT.ball_type}`,
      `**${t('admin.list-id')}**: ${lastCompletedRT.listID}`,
      `**${t('settings.user')}**: ${lastCompletedRT.user}`,
    ].join('\n\n');

  const onVision = machine.tracking_device === TrackingDevice.TrajektVision;

  // only show admins the Stereo Cal date
  const vccLastCompletedContent =
    isAdmin &&
    lastCompletedVcc &&
    `Stereo calibrated on ${format(
      new Date(lastCompletedVcc._changed),
      LOCAL_VERBOSE_DATETIME_FORMAT
    )}`;

  // TAs can see plate calibration date
  const plateCalLastCompletedContent =
    // to be replace by a VCC with a plate calibration date field in future
    machine.last_vision_plate_calibration_changed &&
    `Plate calibrated on ${format(
      new Date(machine.last_vision_plate_calibration_changed),
      LOCAL_VERBOSE_DATETIME_FORMAT
    )}`;

  const visionCalTooltipContent =
    onVision &&
    [plateCalLastCompletedContent, vccLastCompletedContent]
      .filter(Boolean)
      .join('\n\n');

  return (
    <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
      <Heading size="4">{t('admin.hardware-and-firing')}</Heading>
      <Card size="2">
        <Grid
          columns={{
            xs: '2',
            lg: '3',
          }}
          gap="5"
        >
          <LabelledData label="admin.last-fire">
            <Skeleton loading={loadingMachineDetails} width="100%">
              <Text size="2">
                {machine.lastFire
                  ? formatDate(machine.lastFire._created)
                  : t('admin.none')}
              </Text>
            </Skeleton>
          </LabelledData>

          <LabelledData label="admin.last-pitch-fired">
            <Skeleton loading={loadingMachineDetails} width="100%">
              <Text size="2">
                {machine.lastFire?.data.pitch_name
                  ? machine.lastFire.data.pitch_name
                  : t('admin.none')}
              </Text>
            </Skeleton>
          </LabelledData>

          <LabelledData label="admin.hardware-model-and-created-date">
            <Text size="2">
              {machine.hardware_version} - {formatDate(machine._created)}
            </Text>
          </LabelledData>

          <LabelledData label="admin.last-hardware-changed">
            <Skeleton loading={loadingMachineDetails} width="100%">
              <Text size="2">
                {machine.last_hardware_changed
                  ? formatDate(machine.last_hardware_changed)
                  : t('admin.none')}
              </Text>
            </Skeleton>
          </LabelledData>

          <LabelledData label="admin.last-wheel-change">
            <Skeleton loading={loadingMachineDetails} width="100%">
              <Flex gap="2" align="center">
                <Text size="2">
                  {lastWheelChange
                    ? formatDate(lastWheelChange.serviced)
                    : t('admin.none')}
                </Text>

                {!isNil(wheelChangeFires) && (
                  <Badge color={RADIX.COLOR.NEUTRAL}>
                    {wheelChangeFires} fires
                  </Badge>
                )}
              </Flex>
            </Skeleton>
          </LabelledData>

          <LabelledData label="admin.last-ball-change">
            <Skeleton loading={loadingMachineDetails} width="100%">
              <Flex gap="2" align="center">
                <Text size="2">
                  {lastBallChange
                    ? formatDate(lastBallChange.serviced)
                    : t('admin.none')}
                </Text>

                {!isNil(ballChangeShots) && (
                  <Badge color={RADIX.COLOR.NEUTRAL}>
                    {ballChangeShots} shots
                  </Badge>
                )}
              </Flex>
            </Skeleton>
          </LabelledData>

          <LabelledData label="admin.last-completed-repeatability-test">
            <Skeleton loading={loadingMachineDetails} width="100%">
              {lastCompletedRT ? (
                <CommonTooltip
                  trigger={
                    <Flex
                      align="center"
                      gap={RADIX.FLEX.GAP.SM}
                      maxWidth="100%"
                    >
                      <Text size="2" wrap="nowrap">
                        {formatDate(lastCompletedRT._changed)}
                      </Text>
                      <Text truncate>
                        <Badge
                          style={{
                            maxWidth: '100%',
                          }}
                        >
                          <Text
                            truncate
                            style={{
                              lineHeight: 'var(--line-height-1)',
                              letterSpacing: 'var(--letter-spacing-1)',
                            }}
                          >
                            {lastCompletedRT.ball_type}
                          </Text>
                        </Badge>
                      </Text>
                    </Flex>
                  }
                  text_md={repeatabilityTestTooltipContent}
                />
              ) : (
                <Text size="2">{t('admin.none')}</Text>
              )}
            </Skeleton>
          </LabelledData>

          <LabelledData label="admin.users">
            <Skeleton loading={loadingMachineDetails} width="100%">
              <Flex direction="column" gap={RADIX.FLEX.GAP.XS}>
                {!machine.active && !machine.waiting.length && (
                  <Text size="2">{t('admin.none')}</Text>
                )}

                {machine.active && (
                  <Flex gap={RADIX.FLEX.GAP.SM} align="center" width="100%">
                    <MachineUser user={machine.active} />
                    <Badge color={RADIX.COLOR.SUCCESS}>
                      {t('common.active')}
                    </Badge>
                  </Flex>
                )}

                {machine.waiting.map((waitingUser) => (
                  <Flex
                    gap={RADIX.FLEX.GAP.SM}
                    align="center"
                    key={waitingUser.email}
                    width="100%"
                  >
                    <MachineUser user={waitingUser} />
                    <Badge color={RADIX.COLOR.NEUTRAL}>
                      {t('admin.waiting')}
                    </Badge>
                  </Flex>
                ))}
              </Flex>
            </Skeleton>
          </LabelledData>

          <LabelledData label="admin.active-tracking-device">
            <Skeleton loading={loadingMachineDetails} width="100%">
              <Flex gap={RADIX.FLEX.GAP.XS} align="center">
                <Text size="2">{machine.tracking_device}</Text>
                {visionCalTooltipContent && (
                  <CommonTooltip
                    text_md={visionCalTooltipContent}
                    trigger={<InfoCircledIcon />}
                  />
                )}
              </Flex>
            </Skeleton>
          </LabelledData>
        </Grid>
      </Card>
    </Flex>
  );
};
